import * as Sentry from '@sentry/nextjs';
import type { CaptureContext, SeverityLevel } from '@sentry/types';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import { getOrderAppErrorCodeMessage } from '@/errorCodes/orderApp';

import type { ProductVariant } from '@/graphql/saleor/api';

import type {
  ApiErrorWithType,
  OrderAppContext,
  ShoppingBagError,
} from './types';

const shouldSilenceError = (error: Error) => {
  return (
    error.message.startsWith(
      '[GraphQL error]: Query execution error. Link from entry',
    ) && error.message.endsWith('cannot be resolved')
  );
};

export const captureError = (error: Error, context?: CaptureContext) => {
  if (shouldSilenceError(error)) {
    return;
  }

  const errorId = Sentry.captureException(error, context);
  if (process.env.NODE_ENV === 'development') {
    console.error(error);
    return;
  }
  console.error(`Sentry error ID: ${errorId}`);
};

export const captureMessage = (
  error: string,
  context?: CaptureContext | SeverityLevel,
) => {
  const errorId = Sentry.captureMessage(error, context);
  console.error(`Sentry message ID: ${errorId}`);
  console.error(error);
};

export const captureMessageAndSerializeContext = (
  error: string,
  context?: CaptureContext,
) => {
  const errorId = Sentry.captureMessage(error, context);
  console.error(`Sentry message ID: ${errorId}`);
  if (context) {
    console.error(
      'EventPayloadJSON ' +
        JSON.stringify({
          message: error,
          ...(context as object),
        }),
    );
  } else {
    console.error(error);
  }
};

export const capture404Event = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pageName: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  payload: {
    [key: string]: string | object;
    reason: { code: string; message: string };
    requestUrl: string;
  },
) => {
  // disable temporarily until we have a better way to handle 404s
  return;
};

export const handlePromiseErrors = (
  results: PromiseSettledResult<any>[],
): Error[] => {
  const errors: Error[] = [];

  results.forEach((result) => {
    if (result.status === 'rejected') {
      errors.push(result.reason);
    } else if (Array.isArray(result.value)) {
      errors.push(
        ...result.value.filter((item) => item && item.code === 'INVALID'),
      );
    }
  });

  return errors;
};

export const parseErrors = (
  errors: ApiErrorWithType[],
  formatMessage: IntlShape['formatMessage'],
): ShoppingBagError => {
  const parsedErrors = errors
    .map((error) => {
      if (error.context) {
        try {
          const contextObj: OrderAppContext = JSON.parse(error.context);
          const skus = contextObj.skus;
          if (
            error.code === 'stock.cpi_mismatch' ||
            error.code === 'INSUFFICIENT_STOCK'
          ) {
            // Try to parse the reason field to identify problematic products
            let problematicSkus = skus;
            if (contextObj.reason) {
              const reason = contextObj.reason.toLowerCase();
              if (skus.length > 1) {
                // Look for SKUs mentioned in the reason
                const mentionedSkus = skus.filter((sku) =>
                  reason.includes(sku.toLowerCase()),
                );
                if (mentionedSkus.length > 0) {
                  problematicSkus = mentionedSkus;
                } else {
                  problematicSkus = [skus[0]];
                }
              }
            }

            return problematicSkus.reduce((acc, sku) => {
              const mappedMessage = getOrderAppErrorCodeMessage(error);
              acc[sku] = {
                code: error.code,
                message: formatMessage(mappedMessage as MessageDescriptor),
              };
              return acc;
            }, {} as ShoppingBagError);
          }

          return skus.reduce((acc, sku) => {
            const mappedMessage = getOrderAppErrorCodeMessage(error);
            acc[sku] = {
              code: error.code,
              message: formatMessage(mappedMessage as MessageDescriptor),
            };
            return acc;
          }, {} as ShoppingBagError);
        } catch (err) {
          captureMessage(`pase context error ${error.context}`);
          return null;
        }
      }
      return null;
    })
    .filter((item) => item !== null);

  return Object.assign({}, ...parsedErrors);
};

export const getErrorMessage = (
  variant: ProductVariant | undefined,
  errors?: ShoppingBagError,
) => {
  return (variant?.sku && errors?.[variant.sku]?.message) || '';
};
